/**
 * Workspace ("Ask Adrsta" in the sidebar) — chat on the left, canvas on the right.
 *
 * One screen, two engines:
 *   - Study Assistant   → /api/v1/chat (the original AI Assistant): questions about the tenant's
 *     MMM / Geo Lift / Bid Optimizer results, /run-* job creation with inline artifacts.
 *     Routes: /workspace/assistant/:sessionId
 *   - Agent Studio      → /api/v1/studio: Data Analyst, built-in agents, and the user's own agents
 *     (Agent Builder) over uploaded files, with canvases that can be downloaded or shared.
 *     Routes: /workspace/:conversationId
 * A draft chat is /workspace (?agent=assistant | analyst | <agent id>). The two engines keep
 * separate histories, so switching between them starts a new chat.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { message as toast } from "antd";
import { BarChart3, ChevronDown, History, MessageSquarePlus, Sparkles, Trash2, X } from "lucide-react";
import {
  createConversation, deleteAssistantSession, deleteConversation, getAssistantHistory, getConversation, getModels,
  listAgents, listAssistantSessions, listConversations, sendAssistantMessage, sendStudioMessage, updateConversation,
  autoCanvas, uploadAssistantCsv, uploadFile,
  type Agent, type AssistantAttachment, type CanvasData, type ChatModel, type Conversation, type FileInfo,
  type StudioMessage,
} from "./api";
import AgentIcon from "./components/AgentIcon";
import CanvasPanel, { CanvasEmpty } from "./components/canvas/CanvasPanel";
import {
  ColumnMappingArtifact, JobDetails, KNOWN_ARTIFACT_TYPES, type ArtifactItem,
} from "./components/workspace/AssistantArtifacts";
import Composer, { SLASH_CMDS, type Attachment } from "./components/workspace/Composer";
import MessageItem, { type UiMessage } from "./components/workspace/MessageItem";
import PillSelect from "./components/PillSelect";
import "./studio.css";

const ASSISTANT = "assistant"; // Study Assistant (legacy /api/v1/chat engine)
const ANALYST = "analyst"; // Agent Studio general analyst (no agent)

const SUGGESTIONS: Record<string, string[]> = {
  [ASSISTANT]: ["What did my latest MMM find?", "Summarize my Geo Lift experiments", "Which segments have the highest bid multipliers?"],
  [ANALYST]: ["Summarize this dataset and highlight anything unusual", "Chart weekly sales by DMA", "Which channel has the best return on spend?"],
  mmm: ["What did my latest MMM find?", "Where should I move 10% of budget?", "Create a new MMM study"],
  measurement: ["Summarize my latest Geo Lift experiment", "Which markets drove the lift?", "Create a new Geo Lift experiment"],
  bid_optimizer: ["Which segments have the highest bid multipliers?", "List my Bid Optimizer studies", "Create a new Bid Optimizer study"],
};

/** Studio messages carry the raw setup-card payload; the chat renders it as an inline artifact. */
const toUiMessage = (m: StudioMessage): UiMessage => {
  const { artifact, ...rest } = m;
  return KNOWN_ARTIFACT_TYPES.includes(artifact?.type ?? "")
    ? { ...rest, artifact: { id: `${m.id}-art`, type: artifact!.type as ArtifactItem["type"], payload: artifact } }
    : rest;
};

interface HistoryItem {
  key: string;
  kind: "studio" | "assistant";
  id: string;
  title: string;
  subtitle: string;
  updatedAt: string;
}

const newId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const now = () => new Date().toISOString();

export default function WorkspacePage() {
  const { first, second } = useParams();
  const routeSessionId = first === "assistant" ? second : undefined;
  const conversationId = first !== "assistant" ? first : undefined;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [agents, setAgents] = useState<Agent[]>([]);
  const [models, setModels] = useState<ChatModel[]>([]);
  const [defaultModel, setDefaultModel] = useState("");
  const [model, setModel] = useState("");
  const [assistantDefault, setAssistantDefault] = useState<ChatModel | null>(null);
  const [assistantModel, setAssistantModel] = useState(""); // "" = the Study Assistant's own default
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [files, setFiles] = useState<Record<string, FileInfo>>({});
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [liveCanvas, setLiveCanvas] = useState<CanvasData | null>(null);
  const [selectedCanvasId, setSelectedCanvasId] = useState<string | null>(null);
  const [panelArtifact, setPanelArtifact] = useState<ArtifactItem | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const createdHereRef = useRef<string | null>(null); // chat created by our own send; don't reload it
  const assistantFilesRef = useRef<Record<string, AssistantAttachment>>({});
  const scrollRef = useRef<HTMLDivElement>(null);

  // ── Which engine / agent is active ─────────────────────────────────────────
  const draftChoice = searchParams.get("agent") || ASSISTANT;
  const isDraft = !conversationId && !routeSessionId;
  const choice = routeSessionId ? ASSISTANT : conversationId ? conversation?.agentId ?? ANALYST : draftChoice;
  const engine: "studio" | "assistant" = choice === ASSISTANT ? "assistant" : "studio";
  const agent = agents.find((a) => a.id === choice) ?? null;

  const choiceLabel = (value: string) =>
    value === ASSISTANT ? "Study Assistant" : value === ANALYST ? "Data Analyst" : agents.find((a) => a.id === value)?.name ?? "Agent";

  // ── Loading ────────────────────────────────────────────────────────────────
  const refreshHistory = useCallback(async () => {
    const [convs, sessions] = await Promise.all([
      listConversations().catch(() => [] as Conversation[]),
      listAssistantSessions().catch(() => []),
    ]);
    setHistory(
      [
        ...convs.map((c) => ({
          key: `s-${c.id}`, kind: "studio" as const, id: c.id, title: c.title, subtitle: c.agentId ?? ANALYST, updatedAt: c.updatedAt,
        })),
        ...sessions.map((s) => ({
          key: `a-${s.session_id}`, kind: "assistant" as const, id: s.session_id, title: s.name || "Untitled chat",
          subtitle: ASSISTANT, updatedAt: s.last_message_at,
        })),
      ].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
    );
  }, []);

  useEffect(() => {
    listAgents().then(setAgents).catch(() => setAgents([]));
    getModels()
      .then((res) => {
        setModels(res.models);
        setDefaultModel(res.defaultModel);
        setAssistantDefault(res.assistantDefault);
      })
      .catch(() => setModels([]));
    refreshHistory();
  }, [refreshHistory]);

  // Load the chat in the URL, or reset to a fresh draft.
  useEffect(() => {
    const routeId = conversationId ?? routeSessionId;
    if (routeId && routeId === createdHereRef.current) return;
    abortRef.current?.abort();
    setAttachments([]);
    setLiveCanvas(null);
    setPanelArtifact(null);
    setLoadError(null);
    setStreaming(false);

    if (routeSessionId) {
      setConversation(null);
      setSessionId(routeSessionId);
      setSelectedCanvasId(null);
      getAssistantHistory(routeSessionId)
        .then((msgs) =>
          setMessages(
            msgs.map((m, i) => ({
              id: `h-${i}`,
              role: m.role === "assistant" ? "assistant" : "user",
              content: m.content,
              createdAt: m.timestamp ?? now(),
            })),
          ),
        )
        .catch(() => setLoadError("This conversation could not be loaded."));
      return;
    }
    setSessionId(null);
    if (!conversationId) {
      setConversation(null);
      setMessages([]);
      setSelectedCanvasId(null);
      return;
    }
    getConversation(conversationId)
      .then((detail) => {
        setConversation(detail);
        setMessages(detail.messages.map(toUiMessage));
        setFiles(Object.fromEntries(detail.files.map((f) => [f.id, f])));
        setModel(detail.model);
        const lastCanvas = [...detail.messages].reverse().find((m) => m.canvas);
        setSelectedCanvasId(lastCanvas?.id ?? null);
      })
      .catch(() => setLoadError("This conversation could not be loaded."));
  }, [conversationId, routeSessionId]);

  // A saved chat whose model was retired from the picker continues on the default model.
  useEffect(() => {
    if (models.length && model && !models.some((m) => m.id === model)) setModel(defaultModel);
  }, [models, model, defaultModel]);

  // Draft chats start on the default model; saved chats keep the model they last used.
  useEffect(() => {
    if (!conversation && defaultModel) setModel((current) => current || defaultModel);
  }, [conversation, defaultModel]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const canvasVersions = useMemo(
    () =>
      messages
        .filter((m) => m.role === "assistant" && m.canvas && !m.pending)
        .map((m) => ({ ...m, shareable: engine === "studio" })),
    [messages, engine],
  );

  const patchMessage = (id: string, patch: (m: UiMessage) => UiMessage) =>
    setMessages((list) => list.map((m) => (m.id === id ? patch(m) : m)));

  // ── Attachments ────────────────────────────────────────────────────────────
  const handleAttach = (picked: File[]) => {
    if (engine === "assistant") {
      const file = picked[0];
      if (!file) return;
      const localId = newId("att");
      if (!file.name.toLowerCase().endsWith(".csv")) {
        setAttachments([{ localId, name: file.name, status: "error", error: "Only CSV files are supported here" }]);
        return;
      }
      setAttachments([{ localId, name: file.name, status: "uploading" }]);
      uploadAssistantCsv(file)
        .then((data) => {
          assistantFilesRef.current[localId] = data;
          setAttachments([{ localId, name: data.filename, status: "ready", meta: `${data.row_count.toLocaleString()} rows` }]);
          // Same as the original assistant: show the detected column mapping inline.
          setMessages((list) => [
            ...list.filter((m) => m.artifact?.type !== "column_mapping"),
            {
              id: newId("colmap"),
              role: "assistant",
              content: `I've analyzed **${data.filename}** — here's the detected column mapping:`,
              createdAt: now(),
              artifact: { id: newId("art"), type: "column_mapping", payload: data },
            },
          ]);
        })
        .catch((err: Error) =>
          setAttachments([{ localId, name: file.name, status: "error", error: err.message || "Upload failed" }]),
        );
      return;
    }
    for (const file of picked) {
      const localId = newId("att");
      setAttachments((list) => [...list, { localId, name: file.name, status: "uploading" }]);
      uploadFile(file)
        .then((info) =>
          setAttachments((list) => list.map((a) => (a.localId === localId ? { ...a, status: "ready", file: info } : a))),
        )
        .catch((err: Error) =>
          setAttachments((list) =>
            list.map((a) => (a.localId === localId ? { ...a, status: "error", error: err.message } : a)),
          ),
        );
    }
  };

  // ── Send: Agent Studio engine ──────────────────────────────────────────────
  const sendStudio = async (text: string) => {
    const ready = attachments.filter((a) => a.status === "ready" && a.file).map((a) => a.file!);
    const userId = newId("user");
    const replyId = newId("reply");
    setMessages((list) => [
      ...list,
      { id: userId, role: "user", content: text, fileIds: ready.map((f) => f.id), createdAt: now() },
      { id: replyId, role: "assistant", content: "", steps: [], model, createdAt: now(), pending: true },
    ]);
    setFiles((map) => ({ ...map, ...Object.fromEntries(ready.map((f) => [f.id, f])) }));
    setAttachments([]);
    setStreaming(true);
    setLiveCanvas(null);

    const controller = new AbortController();
    abortRef.current = controller;
    let currentReplyId = replyId;
    try {
      let conv = conversation;
      if (!conv) {
        conv = await createConversation({ agentId: choice === ANALYST ? null : choice, model });
        createdHereRef.current = conv.id;
        setConversation(conv);
        navigate(`/workspace/${conv.id}`, { replace: true });
      }
      await sendStudioMessage(
        conv.id,
        { content: text, fileIds: ready.map((f) => f.id), model },
        (e) => {
          switch (e.event) {
            case "user_message":
              patchMessage(userId, () => toUiMessage(e.data));
              break;
            case "token":
              patchMessage(currentReplyId, (m) => ({ ...m, content: m.content + e.data.text }));
              break;
            case "step":
              patchMessage(currentReplyId, (m) => {
                const steps = m.steps ?? [];
                return {
                  ...m,
                  steps: steps.some((s) => s.id === e.data.id)
                    ? steps.map((s) => (s.id === e.data.id ? e.data : s))
                    : [...steps, e.data],
                };
              });
              break;
            case "canvas":
              setLiveCanvas(e.data);
              break;
            case "artifact":
              if (KNOWN_ARTIFACT_TYPES.includes(e.data.type)) {
                const artifact: ArtifactItem = { id: newId("art"), type: e.data.type as ArtifactItem["type"], payload: e.data };
                patchMessage(currentReplyId, (m) => ({ ...m, artifact }));
                if (e.data.type === "job_created") {
                  toast.success(
                    `"${e.data.job_name || "Your study"}" submitted — running in the background. You'll get a notification when it's ready.`,
                  );
                }
              }
              break;
            case "done":
              patchMessage(currentReplyId, () => toUiMessage(e.data));
              currentReplyId = e.data.id;
              setLiveCanvas(null);
              if (e.data.canvas) setSelectedCanvasId(e.data.id);
              break;
            case "error":
              patchMessage(currentReplyId, (m) => ({ ...m, pending: false, error: e.data.message }));
              setLiveCanvas(null);
              break;
          }
        },
        controller.signal,
      );
    } catch (err) {
      const stopped = controller.signal.aborted;
      patchMessage(currentReplyId, (m) => ({
        ...m,
        pending: false,
        error: stopped ? "Stopped. This reply was not saved." : err instanceof Error ? err.message : "Something went wrong.",
      }));
      setLiveCanvas(null);
    } finally {
      setStreaming(false);
      abortRef.current = null;
      refreshHistory();
    }
  };

  // ── Send: Study Assistant engine ──────────────────────────────────────────
  const sendAssistant = async (text: string) => {
    const ready = attachments.find((a) => a.status === "ready");
    const attachment = ready ? assistantFilesRef.current[ready.localId] : undefined;
    const replyId = newId("reply");
    setMessages((list) => [
      ...list,
      { id: newId("user"), role: "user", content: text, attachmentName: attachment?.filename, createdAt: now() },
      { id: replyId, role: "assistant", content: "", createdAt: now(), pending: true },
    ]);
    setAttachments([]);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;
    let replyText = "";
    let chartable = true; // setup cards, errors and quota notices are not laid out as a canvas
    try {
      await sendAssistantMessage(
        { query: text, session_id: sessionId ?? undefined, attachment, model: assistantModel || undefined },
        (event) => {
          switch (event.type) {
            case "session":
              if (!sessionId && event.session_id) {
                createdHereRef.current = event.session_id;
                setSessionId(event.session_id);
                navigate(`/workspace/assistant/${event.session_id}`, { replace: true });
              }
              break;
            case "thinking":
            case "working":
            case "preparing":
              patchMessage(replyId, (m) => ({ ...m, status: { type: event.type, content: event.content || "Working…" } }));
              break;
            case "token":
              replyText += event.content ?? "";
              patchMessage(replyId, (m) => ({ ...m, status: null, content: m.content + (event.content ?? "") }));
              break;
            case "artifact":
              chartable = false;
              if (event.payload && KNOWN_ARTIFACT_TYPES.includes(event.payload.type)) {
                const artifact: ArtifactItem = { id: newId("art"), type: event.payload.type, payload: event.payload };
                patchMessage(replyId, (m) => ({ ...m, artifact }));
                if (event.payload.type === "job_created") {
                  toast.success(
                    `"${event.payload.job_name || "Your job"}" submitted — running in the background. You'll get a notification when it's ready.`,
                  );
                }
              }
              break;
            case "job_complete":
              if (event.payload?.job_config_id) {
                const artifact: ArtifactItem = { id: newId("art"), type: "job_created", payload: event.payload };
                patchMessage(replyId, (m) => ({ ...m, artifact }));
              }
              break;
            case "quota_exceeded":
              chartable = false;
              patchMessage(replyId, (m) => ({ ...m, pending: false, status: null, content: event.content, notice: "quota_exceeded" }));
              setStreaming(false);
              break;
            case "error":
              chartable = false;
              patchMessage(replyId, (m) => ({ ...m, pending: false, status: null, error: event.content || "An error occurred." }));
              setStreaming(false);
              break;
            case "done":
              // The reply is complete; the server may still be persisting the turn before closing the stream.
              patchMessage(replyId, (m) => ({ ...m, pending: false, status: null }));
              setStreaming(false);
              break;
          }
        },
        controller.signal,
      );
    } catch (err) {
      chartable = false;
      const stopped = controller.signal.aborted;
      patchMessage(replyId, (m) => ({
        ...m,
        pending: false,
        status: null,
        error: stopped ? "Stopped." : "I encountered an error. Please try again.",
      }));
    } finally {
      patchMessage(replyId, (m) => (m.pending ? { ...m, pending: false, status: null } : m));
      setStreaming(false);
      abortRef.current = null;
      refreshHistory();
    }
    if (chartable && replyText.trim()) showAutoCanvas(replyId, text, replyText);
  };

  /** Lays out an Study Assistant answer with chartable data on the canvas (not saved with the chat). */
  const showAutoCanvas = (replyId: string, question: string, answer: string) => {
    patchMessage(replyId, (m) => ({ ...m, status: { type: "preparing", content: "Designing canvas…" } }));
    autoCanvas(question, answer, model || undefined)
      .then(({ canvas }) => {
        patchMessage(replyId, (m) => ({ ...m, status: null, canvas }));
        if (canvas) {
          setPanelArtifact(null);
          setSelectedCanvasId(replyId);
        }
      })
      .catch(() => patchMessage(replyId, (m) => ({ ...m, status: null })));
  };

  const handleSend = (text: string) => {
    if (streaming) return;
    return engine === "assistant" ? sendAssistant(text) : sendStudio(text);
  };

  // ── Agent switching, new chat, history ─────────────────────────────────────
  const startNewChat = (withChoice: string) => {
    createdHereRef.current = null;
    navigate(`/workspace?agent=${withChoice}`);
  };

  const changeAgent = async (next: string) => {
    if (next === choice) return;
    // Drafts just switch; a saved Agent Studio chat can switch agents in place; anything else
    // crosses engines, whose histories are separate, so it starts a new chat.
    if (isDraft || !(conversation && next !== ASSISTANT)) {
      startNewChat(next);
      return;
    }
    try {
      setConversation(await updateConversation(conversation.id, { agentId: next === ANALYST ? null : next }));
      refreshHistory();
    } catch {
      setLoadError("The agent could not be changed. Try again.");
    }
  };

  const removeHistoryItem = async (item: HistoryItem) => {
    if (!window.confirm("Delete this conversation?")) return;
    try {
      if (item.kind === "studio") await deleteConversation(item.id);
      else await deleteAssistantSession(item.id);
    } catch {
      setLoadError("The conversation could not be deleted.");
      return;
    }
    refreshHistory();
    if (item.id === conversation?.id || item.id === sessionId) startNewChat(item.kind === "assistant" ? ASSISTANT : ANALYST);
  };

  const openHistoryItem = (item: HistoryItem) => {
    setHistoryOpen(false);
    createdHereRef.current = null;
    navigate(item.kind === "studio" ? `/workspace/${item.id}` : `/workspace/assistant/${item.id}`);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const suggestions = SUGGESTIONS[engine === "assistant" ? ASSISTANT : agent?.templateKey ?? ANALYST] ?? SUGGESTIONS[ANALYST];
  const title =
    engine === "assistant"
      ? history.find((h) => h.id === sessionId)?.title ?? (sessionId ? "Study Assistant" : "New chat")
      : conversation?.title ?? "New chat";
  const headerIcon =
    engine === "assistant" ? <Sparkles className="h-5 w-5" /> : agent ? <AgentIcon icon={agent.icon} className="h-5 w-5" /> : <BarChart3 className="h-5 w-5" />;
  const lastAssistantId = [...messages].reverse().find((m) => m.role === "assistant")?.id;

  const agentOptions = [
    { value: ASSISTANT, label: "Study Assistant", group: "General", icon: <Sparkles className="h-4 w-4" />, hint: "All your MMM, Geo Lift & Bid studies · /run-* setup" },
    { value: ANALYST, label: "Data Analyst", group: "General", icon: <BarChart3 className="h-4 w-4" />, hint: "Analyze files you upload" },
    ...agents.map((a) => ({
      value: a.id,
      label: a.name,
      icon: <AgentIcon icon={a.icon} className="h-4 w-4" />,
      group: a.isBuiltin ? "Built-in agents" : "My agents",
      hint: a.description || undefined,
    })),
  ];

  return (
    <div className="flex h-full min-h-0" style={{ fontFamily: "var(--font-sans)" }}>
      {/* Chat */}
      <section className="flex w-1/2 min-w-[380px] flex-col border-r border-border-subtle-t bg-base">
        <header className="flex items-center gap-2 border-b border-border-subtle-t px-4 py-3">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-adrsta-blue"
            style={{ background: "var(--blue-subtle)" }}
          >
            {headerIcon}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13.5px] font-bold text-primary-t">{title}</div>
            <div className="truncate text-xs text-tertiary-t">{choiceLabel(choice)}</div>
          </div>

          <div className="relative">
            <button
              onClick={() => setHistoryOpen((o) => !o)}
              className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-[13px] font-semibold text-secondary-t hover:bg-hover-bg"
            >
              <History className="h-4 w-4" /> History <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {historyOpen && (
              <div className="absolute right-0 z-30 mt-1 max-h-96 w-80 overflow-y-auto rounded-lg border border-border-default-t bg-elevated py-1 shadow-lg">
                {history.length === 0 && <p className="px-3 py-3 text-[13px] text-tertiary-t">No conversations yet.</p>}
                {history.map((item) => {
                  const active = item.id === conversation?.id || item.id === sessionId;
                  return (
                    <div
                      key={item.key}
                      className="group flex items-center gap-2 px-3 py-2 hover:bg-hover-bg"
                      style={active ? { background: "var(--blue-subtle)" } : undefined}
                    >
                      <button className="min-w-0 flex-1 text-left" onClick={() => openHistoryItem(item)}>
                        <div className="truncate text-[13px] font-semibold text-primary-t">{item.title}</div>
                        <div className="truncate text-xs text-tertiary-t">
                          {choiceLabel(item.subtitle)} · {new Date(item.updatedAt).toLocaleDateString()}
                        </div>
                      </button>
                      <button
                        onClick={() => removeHistoryItem(item)}
                        className="rounded p-1 text-tertiary-t opacity-0 hover:text-[color:var(--error)] group-hover:opacity-100"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <button
            onClick={() => startNewChat(choice)}
            title="New chat"
            className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-[13px] font-semibold text-adrsta-blue hover:bg-hover-bg"
          >
            <MessageSquarePlus className="h-4 w-4" /> New
          </button>
        </header>

        <div ref={scrollRef} className="flex-1 space-y-6 overflow-y-auto px-5 py-6" onClick={() => setHistoryOpen(false)}>
          {loadError && (
            <p className="rounded-lg px-3 py-2 text-[13px]" style={{ background: "var(--error-subtle)", color: "var(--error)" }}>
              {loadError}
            </p>
          )}
          {messages.length === 0 && !loadError && (
            <div className="mx-auto mt-10 max-w-md text-center">
              <div
                className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl text-adrsta-blue"
                style={{ background: "var(--blue-subtle)" }}
              >
                {headerIcon}
              </div>
              <h1 className="mt-4 text-lg font-bold text-primary-t">
                {engine === "assistant" ? "Study Assistant" : agent?.name ?? "What would you like to analyze?"}
              </h1>
              <div className="mt-1 text-[13px] text-secondary-t">
                {engine === "assistant"
                  ? "Ask about any of your MMM, Geo Lift and Bid Optimizer studies, or set up a new one."
                  : agent?.description || "Attach a CSV or Excel file and ask a question. Results appear on the canvas."}
              </div>
              <div className="mt-6 space-y-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => handleSend(s)}
                    disabled={streaming}
                    className="block w-full rounded-card border border-border-default-t bg-surface px-4 py-2.5 text-left text-[13px] text-secondary-t hover:border-adrsta-blue hover:text-primary-t"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <MessageItem
              key={m.id}
              message={m}
              files={files}
              canvasActive={!liveCanvas && m.id === selectedCanvasId}
              onOpenCanvas={(id) => {
                setPanelArtifact(null);
                setSelectedCanvasId(id);
              }}
              artifactHandlers={{
                send: (text) => handleSend(text),
                onView: (path) => navigate(path),
                onDetails: setPanelArtifact,
                interactive: m.id === lastAssistantId && !streaming,
              }}
            />
          ))}
        </div>

        <div className="px-4 pb-4">
          <Composer
            mode={engine}
            initialText={isDraft ? searchParams.get("q") ?? undefined : undefined}
            attachments={attachments}
            streaming={streaming}
            controls={
              <>
                <PillSelect
                  title="Agent"
                  value={choice}
                  onChange={changeAgent}
                  disabled={streaming}
                  menuWidth="w-72"
                  options={agentOptions}
                />
                <PillSelect
                  title="Model"
                  value={engine === "studio" ? model : assistantModel}
                  onChange={engine === "studio" ? setModel : setAssistantModel}
                  disabled={streaming}
                  options={[
                    // The Study Assistant keeps its own default model until another one is picked.
                    ...(engine === "assistant" && assistantDefault
                      ? [{ value: "", label: assistantDefault.label, hint: "Default" }]
                      : []),
                    ...models.map((m) => ({
                      value: m.id,
                      label: m.label,
                      // GPT-5 models run with high reasoning effort (backend models_catalog): slower, more thorough.
                      hint: !m.available
                        ? "Not configured on this server"
                        : m.id.startsWith("gpt-5")
                          ? "Deep reasoning · replies take longer"
                          : undefined,
                      disabled: !m.available,
                    })),
                  ]}
                />
              </>
            }
            onAttach={handleAttach}
            onRemoveAttachment={(id) => {
              delete assistantFilesRef.current[id];
              setAttachments((list) => list.filter((a) => a.localId !== id));
            }}
            onSend={handleSend}
            onStop={() => abortRef.current?.abort()}
          />
        </div>
      </section>

      {/* Canvas / artifact panel */}
      <section className="w-1/2 min-w-[380px] bg-surface">
        {engine === "studio" ? (
          <CanvasPanel
            versions={canvasVersions}
            selectedId={selectedCanvasId}
            liveCanvas={liveCanvas}
            onSelect={setSelectedCanvasId}
            onShareChange={(id, token) => patchMessage(id, (m) => ({ ...m, shareToken: token }))}
          />
        ) : panelArtifact ? (
          <div className="flex h-full flex-col">
            <div className="flex items-center gap-2 border-b border-border-subtle-t px-5 py-3">
              <span className="flex-1 text-[14px] font-bold text-primary-t">
                {panelArtifact.type === "column_mapping" ? "Column mapping" : "Job details"}
              </span>
              <button onClick={() => setPanelArtifact(null)} title="Close" className="rounded p-1 text-tertiary-t hover:bg-hover-bg">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {panelArtifact.type === "column_mapping" ? (
                <ColumnMappingArtifact payload={panelArtifact.payload} />
              ) : (
                <JobDetails payload={panelArtifact.payload} onView={(path) => navigate(path)} />
              )}
            </div>
          </div>
        ) : canvasVersions.length > 0 ? (
          <CanvasPanel
            versions={canvasVersions}
            selectedId={selectedCanvasId}
            liveCanvas={null}
            onSelect={setSelectedCanvasId}
            onShareChange={() => undefined}
          />
        ) : (
          <CanvasEmpty text="Charts from answers, study setup cards, and job details appear here.">
            <div className="mt-5 flex flex-col gap-2">
              {SLASH_CMDS.map((c) => (
                <button
                  key={c.cmd}
                  onClick={() => handleSend(c.cmd)}
                  disabled={streaming}
                  className="rounded-lg border border-border-default-t px-3 py-1.5 text-left text-xs text-secondary-t hover:border-adrsta-blue hover:text-primary-t"
                >
                  {c.icon} <span className="font-mono">{c.cmd}</span> — {c.desc}
                </button>
              ))}
            </div>
          </CanvasEmpty>
        )}
      </section>
    </div>
  );
}
